/**
 * Локализация — §17.5 ТЗ платформы: русский язык, часовой пояс ресторана,
 * рубли, числа с пробелом-разделителем разрядов, даты `ДД.ММ.ГГГГ`.
 *
 * Второго языка нет и не планируется, поэтому здесь нет механизма
 * переключения языков: он был бы слоем, который никто никогда не
 * использует, но который все обязаны поддерживать.
 *
 * Часовой пояс приходит с сервера (`/api/auth/config`, значение `TZ`
 * ресторана) и ставится один раз при загрузке. Пока не пришёл — Москва:
 * ресторан там, и ошибиться на «по умолчанию» безопаснее, чем показать
 * время браузера человеку, который читает график смен.
 */

let restaurantTimeZone = 'Europe/Moscow';

export function setRestaurantTimeZone(timeZone: string): void {
  if (timeZone) {
    restaurantTimeZone = timeZone;
  }
}

export function getRestaurantTimeZone(): string {
  return restaurantTimeZone;
}

/**
 * Число с пробелом-разделителем разрядов: 1 234 567.
 *
 * Пробел именно неразрывный (его и ставит `ru-RU`) — иначе строка
 * «1 234» переносится посреди числа в узкой колонке на телефоне.
 */
export function formatNumber(value: number, fractionDigits = 0): string {
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/** Рубли: 1 234 ₽. Копейки показываются только если они есть. */
export function formatMoney(value: number): string {
  const hasKopecks = Math.round(value * 100) % 100 !== 0;
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: hasKopecks ? 2 : 0,
    maximumFractionDigits: hasKopecks ? 2 : 0,
  }).format(value);
}

/** Дата в формате ДД.ММ.ГГГГ, в часовом поясе ресторана. */
export function formatDate(value: string | Date): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: restaurantTimeZone,
  }).format(toDate(value));
}

/** Дата и время: 26.08.2026, 14:02 — в часовом поясе ресторана. */
export function formatDateTime(value: string | Date): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: restaurantTimeZone,
  }).format(toDate(value));
}

/**
 * Короткая отметка «26.08 14:02» — ровно так, как в макете §13.3 выглядит
 * строка заявки.
 */
export function formatShortDateTime(value: string | Date): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: restaurantTimeZone,
  })
    .format(toDate(value))
    .replace(',', '');
}

function toDate(value: string | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

/** Русское название сервиса — в интерфейсе, а не в коде платформы. */
export const SERVICE_TITLES: Record<string, string> = {
  study: 'Обучение',
  ops: 'Смена',
  finance: 'Финансы',
};

export function serviceTitle(service: string): string {
  return SERVICE_TITLES[service] ?? service;
}

/** Название уровня доступа так, как его понимает человек. */
export const LEVEL_TITLES: Record<string, string> = {
  host: 'владелец',
  admin: 'администратор',
  user: 'сотрудник',
};

export function levelTitle(level: string | null | undefined): string {
  if (!level) return '—';
  return LEVEL_TITLES[level] ?? level;
}
